<script setup>
import { onUnmounted, onMounted, ref, watch } from 'vue'
import { useUserStore } from '../store/user'
import CustomSpinner from '@/components/atoms/CustomSpinner.vue'
import { sendGTagEvent } from '../helpers/gtag'
import { useRoute, useRouter } from 'vue-router'
import { sendEvent } from '@/helpers/cerebro'
import { validatePurchase } from '@/helpers/paddle.js'
import Header from '@/components/molecules/Header.vue'
import { getTransactionDetails } from '@/services/paddleService'
import { useOnboardingStore } from '@/store/onboarding'
import { storeToRefs } from 'pinia'
import ProModal from '@/components/organisms/ProModal.vue'
import AlertIcon from '@/assets/icons/alert-icon.svg'
import { SUPPORT_EMAIL } from '@/constants/general'
const onboardingStore = useOnboardingStore()
const { discountRate, QUIZ_ID, steps } = storeToRefs(onboardingStore)

const status = ref('checking')
const showProModal = ref(false)

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()

const productId = route.query.productId
const source = route.query.src
const price = Number(route.query.pv) / 100
const transactionId = route.query.tid
const currency = route.query.currency
const paymentMethod = route.query.paymentMethod
const cardType = route.query.cardType
const cardLast4 = route.query.cardLast4
const billingCycle = route.query.billingCycle
const customerCountry = route.query.customerCountry
const customerEmail = route.query.customerEmail
const subtotal = route.query.subtotal
const tax = route.query.tax
const discount = route.query.discount
const discountAmount = route.query.discountAmount
const discountApplied = route.query.discountApplied
const revenue = route.query.revenue
const error = route.query.error
const errorCode = route.query.errorCode
const errorMessage = route.query.errorMessage

const checkIfThisPurhcaseIsAlreadyTracked = () => {
  const sid = route.query.session_id
  const pidsString = localStorage.getItem('cdw_pids') || '[]'
  const pids = JSON.parse(pidsString)
  if (pids.indexOf(sid) >= 0) {
    return true
  } else {
    pids.push(sid)
    localStorage.setItem('cdw_pids', JSON.stringify(pids))
    return false
  }
}

const sendPurchaseEvents = async () => {
  const sid = route.query.sessionId
  // If session id is not present, we can't track the purchase
  if (!sid || checkIfThisPurhcaseIsAlreadyTracked(sid)) {
    return
  }
  let transactionDetails
  try {
    transactionDetails = await getTransactionDetails(transactionId)
  } catch (error) {
    console.error('Error while fetching transaction details', error)
  }
  sendEvent({
    eventName: 'purchase',
    properties: {
      product_id: productId ?? transactionDetails.items[0].price?.id,
      price: !isNaN(price)
        ? price
        : parseFloat(transactionDetails.details?.totals?.total / 100),
      currency: currency ?? transactionDetails.currency_code,
      customer_country:
        customerCountry ?? transactionDetails?.address?.country_code ?? '',
      transaction_id: transactionId,
      customer_email: customerEmail ?? transactionDetails.customer?.email ?? '',
      payment_method:
        paymentMethod ??
        transactionDetails.payments[0].method_details?.type ??
        'card',
      card_type:
        cardType ??
        transactionDetails.payments[0]?.method_details?.card?.type ??
        '',
      card_last_4:
        cardLast4 ??
        transactionDetails.payments[0].method_details?.card?.last4 ??
        '',
      billing_cycle:
        billingCycle ??
        `${transactionDetails?.items[0]?.price?.billing_cycle?.frequency ?? ''}_${transactionDetails?.items[0]?.price?.billing_cycle?.interval ?? ''}`,
      subtotal: parseFloat(
        subtotal ?? transactionDetails.details.totals?.subtotal / 100 ?? 0,
      ),
      tax: parseFloat(tax ?? transactionDetails.details.totals?.tax / 100 ?? 0),
      revenue: parseFloat(
        revenue ?? transactionDetails.details?.totals?.earnings / 100 ?? 0,
      ),
      discount_id: discount ?? transactionDetails.discount_id ?? '',
      discount_rate: discountRate.value,
      is_discount_applied:
        discountApplied ??
        (parseInt(transactionDetails.details?.totals?.discount) > 0
          ? '1'
          : '0'),
      discount_amount: parseFloat(
        discountAmount ??
          transactionDetails.details?.totals?.discount / 100 ??
          0,
      ),
      source:
        source ??
        (transactionDetails.details?.totals.discount > 0
          ? 'countdown_discount_1'
          : 'default'),
      quiz_id: QUIZ_ID.value,
    },
  })

  sendGTagEvent({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      value: !isNaN(price)
        ? price
        : parseFloat(transactionDetails.details?.totals?.total / 100),
      currency: 'USD',
      tax: 0.0,
      shipping: 0.0,
      items: [
        {
          item_id: productId ?? transactionDetails.items[0].price?.id,
          item_name: transactionDetails?.items[0].price?.name ?? "",
          item_category: 'One Time Payment',
          item_brand: 'SparkleBook',
          price: !isNaN(price)
            ? price
            : parseFloat(transactionDetails.details?.totals?.total / 100),
          quantity: 1
        }
      ]
    }
  })
}

onMounted(async () => {
  const authUser = await userStore.getCurrentUser()

  sendGTagEvent({ event: 'flow_completed' })

  sendEvent({
    eventName: 'page_displayed',
    properties: {
      page_name: 'verify_payment',
      page_number: steps.value.length + 1
    }
  })

  if (!route.query.success) {
    sendEvent({
      eventName: 'purchase_failed',
      properties: {
        payment_method: paymentMethod,
        source,
        error,
        error_code: errorCode,
        error_message: errorMessage
      }
    })
    sendGTagEvent({ event: 'purchase_failed' })
  }

  if (userStore.isUserPro()) {
    status.value = 'success'
    sendPurchaseEvents(authUser)
  } else {
    sendEvent({
      eventName: 'checking_transaction_in_verify_payment'
    })
    const verificationResponse = await validatePurchase(transactionId)
    if (verificationResponse != null) {
      status.value = 'success'
      sendPurchaseEvents(authUser)
    } else {
      sendEvent({
        eventName: 'purchase_failed',
        properties: {
          payment_method: paymentMethod,
          source,
          error: 'validation_error',
          error_message: "Although paddle payment was successful, we couldn't verify the payment"
        }
      })
      sendGTagEvent({ event: 'purchase_failed' })
      status.value = 'cancelled'
    }
  }
})

onUnmounted(() => {
  sendEvent({
    eventName: 'onboarding_completed',
    properties: { quiz_id: QUIZ_ID.value }
  })

  sendEvent({
    eventName: 'page_completed',
    properties: {
      page_name: 'verify_payment',
      page_number: steps.value.length + 1
    }
  })
})



watch(status, (newStatus) => {
  if (newStatus === "success") {
    showProModal.value = true
    setTimeout(async () => {
      await router.push(
        {
          name: "PaymentSuccess",
        },
        { replace: true },
      );
    }, 3000);
  }
});

const redirectToPaymentSuccess = () => {
  router.push({ name: 'PaymentSuccess' })
}

const redirectToPayment = () => {
  router.push({ name: "Payment" })
}

</script>

<template>
  <Header />
  <ProModal v-if="status === 'success' && showProModal" @close="redirectToPaymentSuccess" />
  <div class="container mt-20">
    <div v-if="status === 'checking'" class="checking-box">
      <p>Validating your payment...</p>
      <CustomSpinner></CustomSpinner>
    </div>
    <div v-if="status === 'cancelled'" class="cancelled-box">
      <AlertIcon class="alert-icon" />

      <p class="subs-info mt-20">We could not verify your payment.</p>
      <p>Try refreshing this page and if the problem persists, contact support at <a href="mailto:{{ SUPPORT_EMAIL }}">{{ SUPPORT_EMAIL }}</a>.</p>
      <button class="btn" @click="redirectToPayment">
        Go back
      </button>
    </div>
  </div>
</template>

<style scoped>
.container {
  padding-bottom: 40px;
  max-height: calc(100vh - 93px);
  overflow: scroll;
}

.checking-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin-top: 50px;
}

.cancelled-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.success-box {
  max-width: 480px;
  margin: 0 auto;
  padding: 8px;
  border-radius: 16px;
  border: 1px solid var(--primary-light);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.neutral-500 {
  color: var(--neutrals-500);
}

img {
  max-width: 100%;
}

.confirmation {
  background: #f5af2a;
  padding: 24px;
  border-radius: 16px 16px 0 0;
  margin: -8px -8px 16px -8px;
}

.activation {
  background: #f5af2a33;
  border: 1px solid #f5af2a;
  width: Fill (343px) px;
  padding: 32px 12px 32px 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 12px;
}

.celebrate-image {
  width: auto;
  height: 330px;
  object-fit: contain;
}

.btn {
  display: flex;
  justify-content: center;
  padding: 12px 24px;
  width: auto;
}

.alert-icon {
  width: 48px;
  height: 48px;
  color: var(--danger-base);
}

</style>
